import { nanoid } from 'nanoid'

import { GlobalEmbedEventEmitter } from './event-handler'

import { type EmbedElementSettings, type EmbedSettings } from './settings'
import { type EmbedTarget } from './embed-targets/embed-target'
import { type DetectEmbedTargetsOptions, type EmbedTargetFactory } from './embed-targets/embed-target-factory'
import { defaultsDeep, debounce, merge } from 'lodash-es'
import { watchForElements } from '../utils/dom'
import { detectEmbedTargetsFromDom, getEmbedTargetFactory } from './embed-targets/utils'
import { ElementEmbedder, type EmbedElementContext } from './element-embedder'
import { ExperienceHooks, PlayerjsHooks, type EmbedHooks } from './hooks'
import { DefaultEmbedStrategyFactoryRegistry, type EmbedStrategyFactoryRegistry } from './embed-strategies/embed-strategy-factory'
import { error, logger, warn } from '../utils/logger'
import { type EmbedderSettings } from './internals/types'
import { fetchDistributionEmbedSettings } from '../utils/fetch'
import { stringOrDomValueExtract } from '../utils/dom-value-extract'
import { DIServiceContainer, type DIServiceMap } from './di/services'

/**
 * A context shared across all instances of embedding.
 * Serves as a base for {@link EmbedElementContext}
 */
interface EmbedderContext {
  settings: EmbedderSettings
  embeddedList: EmbedElementContext[]
  hooks: EmbedHooks[]

  /**
   * A global event handler for all embeds.
   */
  globalEventEmitter: GlobalEmbedEventEmitter
  embedStrategyFactoryRegistry: EmbedStrategyFactoryRegistry
  createElement<K extends keyof HTMLElementTagNameMap> (tagName: K, elementCreationOptions?: ElementCreationOptions, embedderOptions?: { role?: string }): HTMLElementTagNameMap[K]
  getComponentMarkerAttributes (role?: string): Record<string, string>
  isDebugEnabled (property?: string): boolean
  show (): void
  hide (): void

  di: DIServiceContainer
}

/**
 * A core class responsible for performing embedding.
 */
class Embedder {
  /**
   * A context object that is shared across all embeds.
   */
  context: EmbedderContext

  /**
   * Whether the embedder has been initialized.
   */
  initialized: boolean = false

  #resizeHandler?: () => void

  constructor (settings: Partial<EmbedSettings>) {
    this.context = this.#createContext(settings)
    this.context.hooks.forEach(hook => { hook.onEmbedderCreate?.(this.context) })
  }

  #createContext (settings: Partial<EmbedSettings>): EmbedderContext {
    const scope = settings.scope ?? nanoid()
    const attribute = settings.attribute ?? `data-sd-${scope}`
    const mergedSettings = {
      // avoid defining complex fallback object to avoid confusion with defaultsDeep
      ...settings,
      scope,
      env: settings.env ?? 'prod',
      distributionId: settings.distributionId,
      attribute,
      strategy: settings.strategy ?? 'parent',
      classes: settings.classes ?? [],
      style: settings.style ?? {},
      overlayTimeSync: settings.overlayTimeSync ?? true,
      teleport: merge({ query: { maxWidth: 450 } }, settings.teleport)
    }
    const context: EmbedderContext = {
      settings: mergedSettings,
      embeddedList: [],
      hooks: [new PlayerjsHooks(), new ExperienceHooks()],
      globalEventEmitter: new GlobalEmbedEventEmitter(),
      embedStrategyFactoryRegistry: new DefaultEmbedStrategyFactoryRegistry(),
      getComponentMarkerAttributes (role?: string): Record<string, string> {
        return {
          'data-sd-embed-scope': scope,
          ...(role != null ? { 'data-sd-embed-role': role } : {})
        }
      },
      createElement<K extends keyof HTMLElementTagNameMap> (tagName: K, elementCreationOptions?: ElementCreationOptions, embedderOptions?: { role?: string }) {
        const el = document.createElement(tagName, elementCreationOptions)
        const attrs = this.getComponentMarkerAttributes(embedderOptions?.role)
        Object.keys(attrs).forEach(key => {
          el.setAttribute(key, attrs[key])
        })
        return el
      },
      isDebugEnabled (property?: string): boolean {
        if (typeof this.settings.debug === 'boolean') return this.settings.debug
        if (typeof this.settings.debug === 'object') {
          return property != null ? this.settings.debug[property] : true
        }
        return false
      },
      show (): void {
        this.embeddedList.forEach(embedCtx => embedCtx.show())
      },
      hide (): void {
        this.embeddedList.forEach(embedCtx => embedCtx.hide())
      },
      di: new DIServiceContainer()
    }

    if (settings.hooks != null) context.hooks.push(...settings.hooks)
    return context
  }

  #watchResize () {
    this.#resizeHandler = debounce(() => {
      this.context.globalEventEmitter.emit('window.resize', {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight
      })
    }, this.context.settings.windowResizeDebounceMs ?? 100)
    window.addEventListener('resize', this.#resizeHandler)
  }

  #unwatchResize () {
    if (this.#resizeHandler != null) window.removeEventListener('resize', this.#resizeHandler)
  }

  #init (): void {
    if (this.initialized) return
    this.#watchResize()

    this.initialized = true
  }

  async #findTargetElements (embedSettings: EmbedderSettings): Promise<Element[]> {
    const { el, element, selector } = embedSettings
    if (element != null || selector != null) {
      logger.warn('`element` and `selector` is deprecated. Please use `el`.', embedSettings)
    }
    const _selector = el ?? element ?? selector ?? `[${embedSettings.attribute}]`
    const targetEls = await watchForElements(_selector, embedSettings.watch)
    if (targetEls.length === 0) {
      if (typeof _selector === 'string') warn(`selector '${_selector}' did not find any elements.`)
      error('No target element found.', embedSettings)
    }
    return targetEls
  }

  async #embedElement (targetEl: Element, embedSettings: EmbedderSettings, embedTargetFactory: EmbedTargetFactory<EmbedTarget> | undefined): Promise<EmbedElementContext | null> {
    try {
      const elemEmbedder = new ElementEmbedder(this.context, targetEl as HTMLElement, embedSettings, embedTargetFactory)
      return await elemEmbedder.embed()
    } catch (err) {
      error(`[scope=${embedSettings.scope}] Failed to embed element.`, targetEl, err)
      return null
    }
  }

  /**
   * TODO: distinguish what is overridable and what is not
   * @param elementSettings
   * @returns
   */
  async #loadSettings (elementSettings: EmbedElementSettings = {}): Promise<EmbedderSettings> {
    const local: EmbedderSettings = defaultsDeep({}, elementSettings, this.context.settings)
    if (local.skipApiSettingsLoad === true) return local
    let fromApi: { fallback?: Partial<EmbedElementSettings>, settings?: Partial<EmbedElementSettings> } = {}
    try {
      fromApi = await fetchDistributionEmbedSettings<Partial<EmbedElementSettings>>({
        distributionId: local.distributionId,
        clientId: local.clientId != null ? stringOrDomValueExtract(local.clientId) : undefined,
        organizationId: local.organizationId,
        env: local.env
      })
      // priority order:
      // 1. settings passed to `.embed({})`
      // 2. settings from api
      // 3. settings from embedder constructor
      // 4. fallback settings from api
      const merged = defaultsDeep({}, elementSettings, fromApi.settings, this.context.settings, fromApi.fallback)
      logger.debug('Settings loaded from API.', { merged, elementSettings, fromApi, contextSettings: this.context.settings })
      return merged
    } catch (err) {
      logger.error('Error occurred while retrieving settings from api.', local, err)
      return local
    }
  }

  /**
   *
   * @param elementSettings will be merged on top of {@link Embedder.context.settings}
   * @returns a list of {@link EmbedElementContext} of successful embedding.
   */
  async embed (elementSettings: EmbedElementSettings = {}): Promise<EmbedElementContext[]> {
    this.#init()
    const embedSettings = await this.#loadSettings(elementSettings)
    let embedTargetFactory: EmbedTargetFactory<EmbedTarget> | undefined
    if (embedSettings.targetType != null) {
      embedTargetFactory = getEmbedTargetFactory(embedSettings.targetType)
      try {
        await embedTargetFactory?.ready?.()
      } catch (err) {
        logger.error('Waiting for player ready state timed out.', embedTargetFactory?.name, err)
      }
    }

    const targetEls = await this.#findTargetElements(embedSettings)

    const embeddedList: EmbedElementContext[] = []

    for (const targetEl of targetEls) {
      const elEmbedCtx = await this.#embedElement(targetEl, embedSettings, embedTargetFactory)
      if (elEmbedCtx != null) embeddedList.push(elEmbedCtx)
      // TODO cleanup on element removal
    }

    return embeddedList
  }

  detectEmbedTargetsFromDom (opts: DetectEmbedTargetsOptions) {
    return detectEmbedTargetsFromDom(opts)
  }

  registerService (
    ...args: Parameters<DIServiceContainer['register']>
  ) {
    this.context.di.register(...args)
  }

  getRegisteredService<T extends keyof DIServiceMap> (name: T): DIServiceMap[T] | undefined {
    return this.context.di.get<T>(name)
  }

  unregisterService (
    ...args: Parameters<DIServiceContainer['unregister']>
  ) {
    this.context.di.unregister(...args)
  }

  /**
   * Destroys the embedder and all embedded elements.
   * TODO: implement cleanup for each embed
   */
  destroy (): void {
    this.context.embeddedList.forEach(embedCtx => { embedCtx.eventEmitter.destroy() })
    this.#unwatchResize()
    this.context.globalEventEmitter.destroy()
  }

  show (): void {
    this.context.show()
  }

  hide (): void {
    this.context.hide()
  }
}

export {
  Embedder,
  type EmbedderContext
}
